import { z } from 'zod';

import {
  ALLERGY_SEVERITY_ORDER,
  AllergenSchema,
  DietaryPreferenceSchema,
  MEDICAL_ACCESS,
  type Allergen,
  type AllergySeverity,
  type DietaryPreference,
  type NotificationPreset,
  type Role,
} from './enums.js';
import { AllergyInputSchema, type AllergyCardEntry, type DietaryCardEntry } from './player.js';
import { AccountNameSchema } from './name.js';
import { EmailSchema, PhoneSchema } from './primitives.js';

/**
 * Guardians: the invite a coach sends, and the onboarding wizard the invited
 * person walks through (BUILD_PLAN 4.4 and 4.7).
 *
 * One definition for both sides of the wire, like every other schema here.
 * The allergy step is where that matters most: the structured allergen list
 * is what makes the printable card possible, and two copies of it would drift
 * apart on the night a family picks "sesame".
 */

/** Who this adult is to the child. Stored as text; the enum is the boundary. */
export const GuardianRelationshipSchema = z.enum([
  'parent',
  'grandparent',
  'stepparent',
  'guardian',
  'other',
]);
export type GuardianRelationship = z.infer<typeof GuardianRelationshipSchema>;

export const RELATIONSHIP_LABEL: Readonly<Record<GuardianRelationship, string>> = {
  parent: 'Parent',
  grandparent: 'Grandparent',
  stepparent: 'Stepparent',
  guardian: 'Guardian',
  other: 'Other',
};

/**
 * What a coach types to invite a guardian for one player.
 *
 * `confirmedGuardian` is the coach's tick (issue #151). On the Guardians sheet
 * the label is `COACH_GUARDIAN_CONFIRMATION`; on the bulk families sheet it is
 * `BULK_GUARDIAN_CONFIRMATION`, a variant of the same sentence ticked once for
 * the whole paste. A literal true with no default, so an unticked sheet does
 * not parse and the server refuses it. It is recorded in the invite's audit
 * meta.
 */
export const GuardianInviteSchema = z.object({
  email: EmailSchema,
  name: AccountNameSchema.nullish(),
  relationship: GuardianRelationshipSchema,
  isPrimary: z.boolean().default(false),
  confirmedGuardian: z.literal(true),
});
export type GuardianInvite = z.infer<typeof GuardianInviteSchema>;

/**
 * An Owner linking an accepted coach as a parent, without an invitation
 * (ADR-0116). No `isPrimary`: the server decides it, primary only when the
 * child has no live guardian yet, so this route can never demote a parent.
 */
export const GuardianStaffLinkSchema = GuardianInviteSchema.omit({
  email: true,
  name: true,
  isPrimary: true,
}).extend({
  userId: z.uuid(),
});
export type GuardianStaffLink = z.infer<typeof GuardianStaffLinkSchema>;

/**
 * The staff roles an Owner may link as a parent (ADR-0116): the ones that
 * already read a child's medical notes, so the link grants nothing their role
 * lacks. A team manager or scorekeeper is invited by email like any parent.
 * `guardian.test.ts` pins this to `MEDICAL_ACCESS`.
 */
export const STAFF_PARENT_LINK_ROLES: readonly Role[] = ['owner', 'head_coach', 'assistant_coach'];

/** Whether a person holding this role may be linked as a parent by the Owner. */
export function canBeLinkedAsParent(role: string): boolean {
  return (STAFF_PARENT_LINK_ROLES as readonly string[]).includes(role);
}

/**
 * The six steps, in the order BUILD_PLAN 4.7 gives them. A guardianship's
 * `onboardingStep` is the highest step completed, so `ONBOARDING_STEPS` means
 * done and zero means never started.
 */
export const ONBOARDING_STEPS = 6;

export const OnboardingProfileSchema = z.object({
  name: AccountNameSchema,
  phone: PhoneSchema,
});
export type OnboardingProfile = z.infer<typeof OnboardingProfileSchema>;

/** Step two. `confirmed` is a literal so an unchecked box cannot be sent. */
export const OnboardingRelationshipSchema = z.object({
  relationship: GuardianRelationshipSchema,
  confirmed: z.literal(true),
});
export type OnboardingRelationship = z.infer<typeof OnboardingRelationshipSchema>;

export const DietaryInputSchema = z.object({
  preference: DietaryPreferenceSchema,
  note: z.string().trim().max(200).nullable(),
});
export type DietaryInput = z.infer<typeof DietaryInputSchema>;

/**
 * The consent sentence. BUILD_PLAN 4.7's wording named the meal family and
 * the coaches; section 3's role table also gives the team manager and the
 * owner allergy data, and the card admits them, so the sentence names them
 * too (ADR-0071). Exported so the screen renders this string and a test
 * holds it to `MEDICAL_ACCESS`.
 */
export const ALLERGY_CONSENT_TEXT =
  'This information will be shown to the family assigned to bring the team meal, to your coaches, to your team manager, and to the team’s owner. It will not be shown to anyone else or included in any team-wide export.';

/**
 * Step three. An empty list is a real answer ("no allergies"), and it still
 * carries the acknowledgement: the family has read where the data goes.
 *
 * `version` is the `allergyVersion` the screen loaded. A child can have two
 * households (ADR-0070) and the save replaces the whole list, so a screen
 * loaded before the other household's change is refused with STALE_WRITE
 * rather than wiping that change (issue #102, ADR-0102). A counter the client
 * only echoes back. Optional because the app on phones before this change sends
 * none, and that request keeps its old meaning, an unconditional replace.
 */
export const OnboardingAllergiesSchema = z.object({
  allergies: z.array(AllergyInputSchema).max(20),
  dietary: z.array(DietaryInputSchema).max(5),
  consentAcknowledged: z.literal(true),
  version: z.number().int().min(0).max(2_147_483_647).optional(),
});
export type OnboardingAllergies = z.infer<typeof OnboardingAllergiesSchema>;

/** Step four. Both required: a name with no number reaches nobody. */
export const OnboardingEmergencyContactSchema = z.object({
  name: AccountNameSchema,
  phone: PhoneSchema,
});
export type OnboardingEmergencyContact = z.infer<typeof OnboardingEmergencyContactSchema>;

/**
 * Notification categories (BUILD_PLAN 4.8, D18). The three presets lead; the
 * matrix underneath is per category: immediate, a daily digest, or off.
 */
export const NotificationCategorySchema = z.enum([
  'schedule_changes',
  'game_reminders',
  'practice_reminders',
  'announcements',
  'messages',
]);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

export const NotificationLevelSchema = z.enum(['immediate', 'digest', 'off']);
export type NotificationLevel = z.infer<typeof NotificationLevelSchema>;

export type NotificationMatrix = Readonly<Record<NotificationCategory, NotificationLevel>>;

export const CATEGORY_LABEL: Readonly<Record<NotificationCategory, string>> = {
  schedule_changes: 'Schedule changes and cancellations',
  game_reminders: 'Game reminders',
  practice_reminders: 'Practice reminders',
  announcements: 'Team announcements',
  messages: 'Messages from a coach',
};

export const PRESET_LABEL: Readonly<Record<NotificationPreset, { title: string; body: string }>> = {
  everything: { title: 'Everything', body: 'Every reminder and announcement, as it happens.' },
  important_only: {
    title: 'Important only',
    body: 'Schedule changes and coach messages right away. Reminders and announcements in a daily digest.',
  },
  game_reminders_only: {
    title: 'Game reminders only',
    body: 'Game reminders and schedule changes. Nothing else.',
  },
  custom: { title: 'Custom', body: 'Pick a setting for each kind of message.' },
};

/**
 * The matrix a preset stands for, so every consumer reads one shape.
 *
 * `schedule_changes` is immediate in every preset: BUILD_PLAN 4.8 says a
 * schedule change inside 72 hours bypasses the digest regardless, and a
 * preference that claimed otherwise would be a lie on the screen.
 */
export function matrixForPreset(preset: Exclude<NotificationPreset, 'custom'>): NotificationMatrix {
  switch (preset) {
    case 'everything':
      return {
        schedule_changes: 'immediate',
        game_reminders: 'immediate',
        practice_reminders: 'immediate',
        announcements: 'immediate',
        messages: 'immediate',
      };
    case 'important_only':
      return {
        schedule_changes: 'immediate',
        game_reminders: 'digest',
        practice_reminders: 'digest',
        announcements: 'digest',
        messages: 'immediate',
      };
    case 'game_reminders_only':
      return {
        schedule_changes: 'immediate',
        game_reminders: 'immediate',
        practice_reminders: 'off',
        announcements: 'off',
        messages: 'off',
      };
  }
}

const MatrixSchema = z.object({
  schedule_changes: NotificationLevelSchema,
  game_reminders: NotificationLevelSchema,
  practice_reminders: NotificationLevelSchema,
  announcements: NotificationLevelSchema,
  messages: NotificationLevelSchema,
});

/**
 * Step five. A preset alone, or `custom` with the whole matrix. The server
 * stores the materialised matrix either way.
 */
export const OnboardingNotificationsSchema = z.discriminatedUnion('preset', [
  z.object({ preset: z.literal('everything') }),
  z.object({ preset: z.literal('important_only') }),
  z.object({ preset: z.literal('game_reminders_only') }),
  z.object({ preset: z.literal('custom'), categories: MatrixSchema }),
]);
export type OnboardingNotifications = z.infer<typeof OnboardingNotificationsSchema>;

export function matrixFor(input: OnboardingNotifications): NotificationMatrix {
  return input.preset === 'custom' ? input.categories : matrixForPreset(input.preset);
}

/**
 * Step six. A boolean, defaulting to false on the row (rubric B9) and never
 * defaulted here: the screen sends what the guardian chose, and the
 * unchecked box is a real answer rather than an omission.
 *
 * `previousConsent` is the value the screen loaded. Consent is on the child,
 * shared by every household, so a screen loaded before another household's
 * change is refused with STALE_WRITE rather than reversing it (ADR-0102).
 * Optional for the same reason as `version` above: absent, consent is set
 * unconditionally, as before.
 */
export const OnboardingPhotoConsentSchema = z.object({
  consent: z.boolean(),
  previousConsent: z.boolean().optional(),
});
export type OnboardingPhotoConsent = z.infer<typeof OnboardingPhotoConsentSchema>;

/**
 * Who may open the allergy card (BUILD_PLAN 4.7): the coaches, and the family
 * assigned to bring the meal. There is no meal rotation yet, so today it is
 * the roles `MEDICAL_ACCESS` grants allergies to, and a guardian is refused.
 * When meals ship, the assignment row becomes the second gate, not this one.
 */
export function canViewAllergyCard(role: Role): boolean {
  return MEDICAL_ACCESS[role].allergies;
}

export const ALLERGEN_LABEL: Readonly<Record<Allergen, string>> = {
  peanut: 'Peanut',
  tree_nut: 'Tree nuts',
  dairy: 'Dairy',
  egg: 'Egg',
  soy: 'Soy',
  wheat_gluten: 'Wheat and gluten',
  fish: 'Fish',
  shellfish: 'Shellfish',
  sesame: 'Sesame',
  other: 'Other',
};

export const DIETARY_LABEL: Readonly<Record<DietaryPreference, string>> = {
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
  halal: 'Halal',
  kosher: 'Kosher',
  other: 'Other',
};

export const SEVERITY_LABEL: Readonly<Record<AllergySeverity, string>> = {
  avoid_preference: 'Prefers to avoid',
  intolerance: 'Intolerance',
  allergy: 'Allergy',
  severe_anaphylaxis: 'Severe: anaphylaxis',
};

/** Severe enough that the card escalates it visually, not only by colour. */
export function isSevere(severity: AllergySeverity): boolean {
  return severity === 'severe_anaphylaxis';
}

export function severityRank(severity: AllergySeverity): number {
  return ALLERGY_SEVERITY_ORDER.indexOf(severity);
}

/** What each allergen means in a kitchen, for the "avoid" list. */
const AVOID_NOTE: Readonly<Record<Exclude<Allergen, 'other'>, string>> = {
  peanut: 'Peanuts and peanut butter, including in sauces and baked goods',
  tree_nut: 'Tree nuts: almonds, cashews, walnuts, pecans, and anything made with them',
  dairy: 'Milk, cheese, butter, yogurt, and cream',
  egg: 'Eggs, including mayonnaise and most baked goods',
  soy: 'Soy, tofu, edamame, and soy sauce',
  wheat_gluten: 'Wheat and gluten: bread, pasta, pizza crust, and most baked goods',
  fish: 'Fish of any kind',
  shellfish: 'Shellfish: shrimp, crab, lobster, and clams',
  sesame: 'Sesame seeds, tahini, and hummus',
};

/** Common team meals and what they usually contain. */
const COMMON_MEALS: readonly { readonly name: string; readonly contains: readonly Allergen[] }[] = [
  { name: 'Fresh fruit', contains: [] },
  { name: 'Veggie sticks', contains: [] },
  { name: 'Grilled chicken', contains: [] },
  { name: 'Rice bowls', contains: [] },
  { name: 'Tacos on corn tortillas', contains: [] },
  { name: 'Pasta with tomato sauce', contains: ['wheat_gluten'] },
  { name: 'Sandwiches and subs', contains: ['wheat_gluten'] },
  { name: 'Cheese pizza', contains: ['wheat_gluten', 'dairy'] },
  { name: 'Yogurt', contains: ['dairy'] },
  { name: 'Hummus', contains: ['sesame'] },
  { name: 'Granola bars', contains: ['tree_nut', 'peanut', 'wheat_gluten', 'soy'] },
  { name: 'Peanut butter sandwiches', contains: ['peanut', 'wheat_gluten'] },
];

/**
 * The "safe to bring" and "avoid" lists (BUILD_PLAN 4.7), generated from the
 * allergen list and never from anything a guardian typed. An `other` allergen
 * contributes its own name to the avoid list and nothing to the safe list,
 * because this function cannot know what a kitchen item contains that it has
 * never heard of.
 */
export function allergyCardSummary(
  entries: readonly AllergyCardEntry[],
  dietary: readonly DietaryCardEntry[] = [],
): {
  readonly safeToBring: readonly string[];
  readonly avoid: readonly string[];
} {
  const present = new Set<Allergen>();
  const others = new Set<string>();
  for (const entry of entries) {
    present.add(entry.allergen);
    if (entry.allergen === 'other' && entry.allergenOther !== null) {
      others.add(entry.allergenOther);
    }
  }

  const avoid: string[] = [];
  for (const allergen of AllergenSchema.options) {
    if (allergen === 'other' || !present.has(allergen)) continue;
    avoid.push(AVOID_NOTE[allergen]);
  }
  for (const other of others) avoid.push(other);

  /*
   * EMPTY when any allergen is outside the known set, named or not. Nothing
   * in `COMMON_MEALS` can declare that it contains "strawberry", so a list
   * generated past an `other` entry told a kitchen that fresh fruit was safe
   * beside an avoid line naming strawberry, for an anaphylaxis case. The
   * screen says why the list is absent. The same gate for any food
   * preference: `COMMON_MEALS` knows allergens, not diets, so a vegan
   * child's card listed grilled chicken as safe beside "Vegan" (ADR-0071).
   */
  const safeToBring =
    present.has('other') || dietary.length > 0
      ? []
      : COMMON_MEALS.filter((meal) => !meal.contains.some((allergen) => present.has(allergen))).map(
          (meal) => meal.name,
        );

  return { safeToBring, avoid };
}
